import React, {useState, useEffect, useRef} from "react"
import {useDispatch, useSelector} from "react-redux"
import {getCommonDetails} from "redux/actions"
import {useNavigate} from "react-router-dom"

import SearchIcon from "assets/icons/search.svg"
import NextIcon from "assets/icons/next-arrow.svg"
import LocationIcon from "assets/icons/location2.svg"
import "./location-theme2.scss"
import {GoogleMap, Marker, InfoWindow} from "@react-google-maps/api"
import {getLocalStorageValue, setLocalStorageValue} from "config/helper"
import SliderDots from "routes/weekly-ads/WeeklySingleAds/SliderDots"
import {useApiDefaults} from "hooks/useApiDefaults"
import MapErrorBoundary from "./MapErrorBoundary"
import useGoogleMaps from "hooks/useGoogleMaps"


const PhoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"
      fill="currentColor"
    />
  </svg>
)

const EmailIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
      fill="currentColor"
    />
  </svg>
)

const STORES_PER_PAGE = 6

const FindLocation2 = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const storeList = useSelector(({store}) => store.storeListData)

  const formatPhoneNumber = str => {
    let cleaned = ("" + str).replace(/\D/g, "")
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3]
    }
    return null
  }

  const mapStyles = {
    height: "600px",
    width: "100%",
  }

  const [searchText, setSearchText] = useState("")
  const [filteredStoreList, setFilteredStoreList] = useState([])
  const [activeDots, setActiveDots] = useState(1)
  const clientId = getLocalStorageValue("RSAclient-id");
  

  function handleSearch(e) {
    let q = e.target.value
    setSearchText(q)
    const fields = ["AddressLine1", "AddressLine2", "City", "ZipCode"]

    q = q.toLowerCase()

    const items = storeList
      .map(item => {
        const exactMatch = fields.some(
          field => item[field]?.toLowerCase() === q
        )
        const fuzzyMatch = fields.some(field =>
          item[field]?.toLowerCase().includes(q)
        )

        if (!exactMatch && !fuzzyMatch) return null

        return {
          item,
          score: exactMatch ? 1 : 0,
        }
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .map(entry => entry.item)

    setFilteredStoreList(items)
    setActiveDots(1)
  }

  const totalItems = searchText ? filteredStoreList : storeList
  const dots = Array.from(
    {
      length:
        totalItems.length > 0
          ? Math.ceil(totalItems.length / STORES_PER_PAGE)
          : 0,
    },
    (_, i) => i + 1
  )

  const handleActiveDot = dot => setActiveDots(dot)
  const onPrev = () => setActiveDots(activeDots - 1)
  const onNext = () => setActiveDots(activeDots + 1)

  const indexofLastItem = activeDots * STORES_PER_PAGE
  const indexofFirsttItem = indexofLastItem - STORES_PER_PAGE
  const currentItems = totalItems
    ? totalItems.slice(indexofFirsttItem, indexofLastItem)
    : []

  const apiDefaults = useApiDefaults()
  function handleStoreSelection(store) {
    const clientStoreId = store.ClientStoreId

    setLocalStorageValue("selected-store-id", clientStoreId)
    setLocalStorageValue("selected-initial-store-id", clientStoreId)

    const aogEndPointURL = getLocalStorageValue("api-end-point-aog")
    if (aogEndPointURL) {
      const body = {
        ...apiDefaults,
        ClientStoreId: parseInt(clientStoreId),
      }
      dispatch(getCommonDetails(body))
    }
  }

  function handleViewWeeklyAdClick(store) {
    handleStoreSelection(store)

    const clientStoreId = store.ClientStoreId
    const clientStoreName = encodeURIComponent(store.ClientStoreName)
    const url = `/weekly-ads/${clientStoreId}/${clientStoreName}`
    navigate(url)
  }

  function handleViewLocationInfoClick(store) {
    navigate(`/locations?store=${store.ClientStoreId}`)
  }

  function ShowGoogleMap() {
    const { isLoaded, loadError } = useGoogleMaps()
    const [hoveredStore, setHoveredStore] = useState(null)
    const hoverTimeoutRef = useRef(null)
    const isHoveringInfoWindow = useRef(false)
    const [bounds, setBounds] = useState(null)

    useEffect(() => {
      if (isLoaded && window.google && storeList.length) {
        const newBounds = new window.google.maps.LatLngBounds()
        storeList.forEach(store => {
          newBounds.extend({
            lat: parseFloat(store.Latitude),
            lng: parseFloat(store.Longitude),
          })
        })
        setBounds(newBounds)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeList, isLoaded])

    const onLoad = map => {
      if (bounds) {
        map.fitBounds(bounds)
      }
    }

    const handleMarkerMouseOver = store => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      setHoveredStore(store)
    }

    const handleMarkerMouseOut = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      if (!isHoveringInfoWindow.current) {
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredStore(null)
        }, 200)
      }
    }

    const handleInfoWindowMouseEnter = () => {
      isHoveringInfoWindow.current = true
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }

    const handleInfoWindowMouseLeave = () => {
      isHoveringInfoWindow.current = false
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredStore(null)
      }, 200)
    }

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current)
        }
      }
    }, [])

    const MapStoreInfoCard = ({store}) => (
      <div
        className="map-store-info-card"
        onMouseEnter={handleInfoWindowMouseEnter}
        onMouseLeave={handleInfoWindowMouseLeave}
      >
        <h3>{store.ClientStoreName}</h3>
        <p>{store.AddressLine1}</p>
        {store.AddressLine2 && <p>{store.AddressLine2}</p>}
        <p>
          {store.City}, {store.StateName}
        </p>
        {store.StorePhoneNumber && (
          <p className="contact-info">
            <span className="icon">
              <PhoneIcon />
            </span>
            <a href={`tel:${store.StorePhoneNumber}`}>
              {formatPhoneNumber(store.StorePhoneNumber)}
            </a>
          </p>
        )}
        {store.StoreEmail && (
          <p className="contact-info">
            <span className="icon">
              <EmailIcon />
            </span>
            <a href={`mailto:${store.StoreEmail}`}>{store.StoreEmail}</a>
          </p>
        )}
      </div>
    )

    if (!isLoaded) {
      return (
        <div className="store-service-location-section">
          <div className="store-service-location-section-map">
            <div className="map-image">
              <div className="map-loading">Loading map...</div>
            </div>
          </div>
        </div>
      )
    }

    if (loadError) {
      return (
        <div className="store-service-location-section">
          <div className="store-service-location-section-map">
            <div className="map-image">
              <div className="map-error">Failed to load Google Maps</div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="store-service-location-section">
        <div className="store-service-location-section-map">
          <div className="map-image">
            <MapErrorBoundary>
              <GoogleMap
                mapContainerStyle={mapStyles}
                onLoad={onLoad}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                }}
              >
                {storeList.map(store => (
                  <Marker
                    key={store.ClientStoreId}
                    position={{
                      lat: parseFloat(store.Latitude),
                      lng: parseFloat(store.Longitude),
                    }}
                    label={{
                      text: store.ClientStoreId.toString(),
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                    onMouseOver={() => handleMarkerMouseOver(store)}
                    onMouseOut={handleMarkerMouseOut}
                  />
                ))}
                {hoveredStore && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(hoveredStore.Latitude),
                      lng: parseFloat(hoveredStore.Longitude),
                    }}
                    onCloseClick={() => setHoveredStore(null)}
                    options={{
                      pixelOffset: new window.google.maps.Size(0, -40),
                      disableAutoPan: true,
                      shouldFocus: false,
                    }}
                  >
                    <MapStoreInfoCard store={hoveredStore} />
                  </InfoWindow>
                )}
              </GoogleMap>
            </MapErrorBoundary>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="container">
        <div className="location-section-theme2">
          <div className="find-store-tile">
            {clientId == 347 ? <h3>Find a School</h3> : <h3>Find a store</h3>}
            <span>Search by street address, city, or zip code</span>
          </div>
          <div>
            <div className="location-search-box">
              <form
                className="location-search-form"
              >
                <input
                  type="location-search"
                  name="q"
                  placeholder="Enter location"
                  value={searchText}
                  onChange={handleSearch}
                  required
                />

                <button
                  type="button"
                  className="location-search-btn"
                  aria-label="Search"
                >
                  {" "}
                  <img src={SearchIcon} alt="SearchIcon" />
                </button>
              </form>
            </div>
          </div>
          <div className="store-service-location-section-item">
            <div className="store-card-section">
              <div className="store-card-wrap">
                {currentItems?.map(store => (
                  <div className="store-cards" key={store.store_id}>
                    <div className="store-card">
                      <div className="store-header">
                        <span className="store-number">
                          <img src={LocationIcon} alt="icon" />
                        </span>
                        <div className="store-info">
                          <h3>{`${store.ClientStoreName}`}</h3>
                          {store.AddressLine1 && <p>{store.AddressLine1}</p>}
                          {store.AddressLine2 && <p>{store.AddressLine2}</p>}
                          <p>
                            {store.City}, {store.StateName} - {store.ZipCode}
                          </p>
                        </div>
                      </div>

                      <div className="store-body">
                        <p>
                          <a
                            href={`tel:${store.StorePhoneNumber}`}
                            rel="noreferrer"
                          >
                            {" "}
                            Phone: {formatPhoneNumber(store.StorePhoneNumber)}
                          </a>
                        </p>
                        {store?.fax_number && (
                          <p>FAX Number: {store.fax_number}</p>
                        )}
                        <button
                          className="select-btn"
                          type="button"
                          onClick={() => handleStoreSelection(store)}
                        >
                          Continue to shop here
                        </button>
                      </div>
                      <div
                        className="link-section"
                        onClick={() => handleViewWeeklyAdClick(store)}
                      >
                        {clientId == 347 ? <span>View Upcoming Events</span> : <span>View Weekly Ad</span>}{" "}
                        <img
                          src={NextIcon}
                          alt="next icon"
                          style={{height: "20px", width: "20px"}}
                        />
                      </div>
                        {clientId == 347 ? "" : 
                          <div
                        className="link-section view-detail"
                        onClick={() => handleViewLocationInfoClick(store)}
                      >
                        <span>View Store Details</span>{" "}
                        <img
                          src={NextIcon}
                          alt="next icon"
                          style={{height: "20px", width: "20px"}}
                        />
                      </div>
                        }
                    
                    </div>
                  </div>
                ))}
              </div>
              {totalItems.length ? (
                <SliderDots
                  pages={dots.length}
                  activeSlide={activeDots}
                  handleActiveDot={handleActiveDot}
                  onPrev={onPrev}
                  onNext={onNext}
                />
              ) : null}
            </div>
            <ShowGoogleMap />
          </div>
        </div>
      </div>
    </>
  )
}
export default FindLocation2
